// src/pages/AllBlogs.js
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowRight, BookOpen } from 'react-feather';

const AllBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const imageBaseUrl = 'https://dawamaxma.dawamtaani.co.ke';

  useEffect(() => {
    fetch('https://dawamaxma.dawamtaani.co.ke/api/blogs')
      .then((response) => response.json())
      .then((data) => {
        setBlogs(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching blogs:', error);
        setLoading(false);
      });
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-primary-600 font-bold tracking-wide uppercase text-sm mb-3">Our Journal</h2>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-neutral-900 mb-6">
              Insights & Health <span className="text-primary-600">Stories</span>
            </h1>
            <div className="w-24 h-1.5 bg-primary-500 mx-auto rounded-full mb-8"></div>
            <p className="text-xl text-neutral-600 max-w-2xl mx-auto">
              Stay updated with the latest in healthcare, wellness tips, and pharmacy news from DawaMtaani.
            </p>
          </motion.div>
        </div>

        {/* Blogs Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid gap-8 md:grid-cols-2 lg:grid-cols-3"
        >
          {blogs.map((blog) => (
            <motion.div
              key={blog.id}
              variants={cardVariants}
              whileHover={{ y: -10 }}
              className="bg-white rounded-2xl shadow-lg overflow-hidden border border-neutral-100 flex flex-col group transition-all duration-300 hover:shadow-2xl"
            >
              <div className="relative h-64 overflow-hidden">
                <img
                  src={blog.image ? `${imageBaseUrl}/${blog.image}` : 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?q=80&w=800'}
                  alt={blog.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute top-4 left-4">
                  <span className="bg-primary-500 text-white text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider">
                    Health
                  </span>
                </div>
              </div>

              <div className="p-8 flex flex-col flex-1">
                <div className="flex items-center text-xs text-neutral-400 mb-4 space-x-4">
                  <div className="flex items-center">
                    <Calendar className="w-3.5 h-3.5 mr-1.5 text-primary-400" />
                    {new Date(blog.created_at || Date.now()).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                  <div className="flex items-center">
                    <User className="w-3.5 h-3.5 mr-1.5 text-primary-400" />
                    Admin
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-neutral-900 mb-3 group-hover:text-primary-600 transition-colors line-clamp-2">
                  {blog.title}
                </h2>
                <p className="text-neutral-600 mb-6 line-clamp-3 flex-1">
                  {blog.subheading || blog.content?.substring(0, 120)}
                </p>

                <div className="pt-6 border-t border-neutral-50 mt-auto">
                  <Link
                    to={`/blogs/${blog.id}`}
                    className="inline-flex items-center text-primary-600 font-bold group/btn"
                  >
                    Read Full Story
                    <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover/btn:translate-x-2" />
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {blogs.length === 0 && (
          <div className="text-center py-20 bg-white rounded-3xl shadow-sm border border-neutral-100">
            <BookOpen className="w-16 h-16 text-neutral-200 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-neutral-400">No blog posts found yet.</h3>
            <p className="text-neutral-400 mt-2">Check back later for fresh health insights!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllBlogs;
